import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../ui/Button/Button';
import Card from '../../../ui/Card';
import DataTable from '../../../ui/DataTable/DataTable';
import { StatGrid } from '../../../ui/Stat/Stat';
import { Input, Select, Checkbox } from '../../../ui/Field/Field';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { finesService, errorMessage } from '../../../services/finesService';
import { Field, Pill, StatCard } from './ui';

const HISTORY_COLUMNS = [
    { key: 'run_id', header: '#', type: 'id' },
    { key: 'run_date', header: 'Date', cell: (r) => <span className="ds-cell-date">{r.run_date}</span> },
    { key: 'started_at', header: 'Started', cell: (r) => <span className="ds-cell-date">{r.started_at}</span> },
    { key: 'total_fines', header: 'Fines', type: 'number' },
    { key: 'new_fines', header: 'New', type: 'number' },
    { key: 'paid_fines', header: 'Paid', type: 'number' },
    { key: 'unassigned_fines', header: 'Unassigned', type: 'number' },
    { key: 'images', header: 'Images', sortable: false, cell: (r) => <span className="ds-cell-num">{`${r.screenshots_portal ?? 0} / ${r.screenshots_offline ?? 0}`}</span> },
];

export default function RunsTab() {
    const [mode, setMode] = useState('auto');
    const [limit, setLimit] = useState('');
    const [backfill, setBackfill] = useState(false);
    const [dry, setDry] = useState(false);
    const [live, setLive] = useState([]);
    const [history, setHistory] = useState([]);
    const [watcher, setWatcher] = useState(null);
    const [selected, setSelected] = useState(null);
    const [log, setLog] = useState([]);
    const [error, setError] = useState(null);
    const selectedId = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const r = await finesService.runs();
            setLive(r.live); setHistory(r.history); setWatcher(r.watcher);
            const current = r.live.find((x) => x.run_id === selectedId.current) || r.active || r.live[0] || null;
            selectedId.current = current?.run_id || null;
            setSelected(current);
            if (current) setLog((await finesService.runLog(current.run_id)).lines);
            setError(null);                                  // an old error never stays after a good read
            return (r.live || []).length > 0;
        } catch (e) { setError(errorMessage(e)); return false; }
    }, []);
    // the next read starts after the last answer (never piled up): every 3 s while a run is live, 15 s when idle
    const pollNow = useRef(() => {});
    useEffect(() => {
        let alive = true; let t = null; let gen = 0;
        const tick = async () => {
            clearTimeout(t);
            const mine = ++gen;                              // one chain only: an older read never schedules again
            const live = await refresh();
            if (alive && mine === gen) t = setTimeout(tick, live ? 3000 : 15000);
        };
        pollNow.current = tick;                              // a run just started: followed at once, at 3 s
        tick();
        return () => { alive = false; clearTimeout(t); pollNow.current = () => {}; };
    }, [refresh]);

    const start = async () => {
        setError(null);
        try {
            const s = await finesService.startRun({ screenshot_mode: mode, limit: limit ? Number(limit) : undefined, dry_run: dry, backfill_images: backfill });
            selectedId.current = s.run_id; pollNow.current();
        } catch (e) { setError(errorMessage(e)); }
    };
    const busy = live.some((r) => r.state === 'running' || r.state === 'queued');
    const state = (s) => s === 'done' ? <Pill tone="green">Done</Pill> : s === 'failed' ? <Pill tone="red">Failed</Pill> : <Pill tone="amber">Running</Pill>;

    return (
        <div className="space-y-5">
            <Card>
                <div className="flex flex-wrap items-end gap-3">
                    <Field label="Image mode">
                        <Select value={mode} onChange={(e) => setMode(e.target.value)} options={[
                            { value: 'auto', label: 'auto (RTA portal, offline fallback)' },
                            { value: 'portal', label: 'portal only' },
                            { value: 'offline', label: 'offline cards' },
                            { value: 'none', label: 'no images' },
                        ]} />
                    </Field>
                    <Field label="Image limit"><Input type="number" min={1} value={limit} onChange={(e) => setLimit(e.target.value)} /></Field>
                    <Checkbox label="Backfill missing images" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} />
                    <Checkbox label="Dry run (no changes)" checked={dry} onChange={(e) => setDry(e.target.checked)} />
                    <Button variant="primary" leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />} onClick={start} disabled={busy}>Run now</Button>
                    {watcher && <span className="text-xs text-gray-600 ml-auto">{watcher.fines_source === 'portal' ? `RTA portal sync daily at ${watcher.portal_daily_time} (${watcher.portal_timezone})${watcher.portal_last_date ? `, last ${watcher.portal_last_date}` : ''} · ` : ''}Auto-import {watcher.enabled ? 'on' : 'off'} · every {watcher.interval_s}s · last check {watcher.last_check || '—'}{watcher.last_reason ? ` · ${watcher.last_reason}` : ''}</span>}
                </div>
            </Card>
            {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {selected && (
                <Card>
                    <div className="flex items-center gap-3 flex-wrap"><strong className="font-mono text-gray-900">{selected.run_id}</strong>{state(selected.state)}<span className="text-gray-500 text-sm">{selected.started_at} → {selected.finished_at || '…'}</span></div>
                    {selected.error && <div role="alert" className="text-sm text-red-700 mt-2">{selected.error}</div>}
                    {selected.report && (
                        <StatGrid min={150} className="mt-3">
                            <StatCard label="Fines" value={selected.report.total_fines} tone="slate" />
                            <StatCard label="New" value={selected.report.new_fines} tone="slate" />
                            <StatCard label="Re-assigned" value={selected.report.reassigned} tone="slate" />
                            <StatCard label="Paid detected" value={selected.report.paid_fines} tone="slate" />
                            <StatCard label="Unassigned" value={selected.report.unassigned} tone="slate" />
                            <StatCard label="Images portal / offline / failed" value={`${selected.report.screenshots_portal} / ${selected.report.screenshots_offline} / ${selected.report.screenshot_failures}`} tone="slate" />
                        </StatGrid>
                    )}
                    <pre className="mt-3 bg-gray-900 text-gray-100 text-xs p-3 rounded-lg max-h-80 overflow-auto text-left" dir="ltr" aria-label="Run log">{log.join('')}</pre>
                </Card>
            )}
            <DataTable
                title="Run history"
                columns={HISTORY_COLUMNS}
                data={history}
                rowKey="run_id"
                searchable={false}
                columnToggle={false}
                emptyIcon={ICONS.history}
                emptyTitle="No runs yet"
            />
        </div>
    );
}
